import { useTranslation } from "react-i18next";
import { useState } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faChevronLeft, faChevronRight } from "@fortawesome/pro-solid-svg-icons";
import { useQuery } from "@tanstack/react-query";
import { cn } from "frontend-components";

import type { TaskListWithTaskOccurrences } from "@dashboard/api/task";
import { userByIdOptions } from "@dashboard/api/queryApi";
import { Group } from "@dashboard/features/Dashboard/Lists/parts/Group";
import { getListTaskCounts } from "@dashboard/features/Dashboard/Lists/utils";

interface ListViewBodyProps {
    list: TaskListWithTaskOccurrences;
}

export function ListViewBody({ list }: ListViewBodyProps) {
    const { t } = useTranslation();
    const [sidebarOpen, setSidebarOpen] = useState(true);

    const { completedTasks, totalTasks } = getListTaskCounts(list);
    const isCompleted = totalTasks > 0 && completedTasks === totalTasks;
    const progressPercent = totalTasks > 0 ? Math.round((completedTasks / totalTasks) * 100) : 0;

    const hasNoTasks = list.tasks.length === 0 && list.groups.length === 0;

    return (
        <div
            className={`-mx-6 -mb-6 md:flex-1 md:min-h-0 grid transition-[grid-template-columns] duration-300 ease-in-out ${
                sidebarOpen
                    ? "grid-cols-1 md:grid-cols-[minmax(0,1fr)_320px]"
                    : "grid-cols-1 md:grid-cols-[minmax(0,1fr)_64px]"
            }`}
        >
            <div className="flex flex-col gap-4 p-6 md:overflow-y-auto md:min-h-0">
                {hasNoTasks ? (
                    <p className="text-body italic text-text-secondary">
                        {t("common.noTasksTitle")}
                    </p>
                ) : (
                    <>
                        {list.tasks.length > 0 && (
                            <Group tasks={list.tasks} title={t("common.tasks")} />
                        )}
                        {list.groups.map((group) => (
                            <Group key={group.id} tasks={group.tasks} title={group.title} />
                        ))}
                    </>
                )}
            </div>

            <DetailSidebar
                open={sidebarOpen}
                onToggle={() => setSidebarOpen((prev) => !prev)}
                list={list}
                isCompleted={isCompleted}
                progressPercent={progressPercent}
                completedTasks={completedTasks}
                totalTasks={totalTasks}
            />
        </div>
    );
}

function DetailSidebar({
    open,
    onToggle,
    list,
    isCompleted,
    progressPercent,
    completedTasks,
    totalTasks,
}: {
    open: boolean;
    onToggle: () => void;
    list: TaskListWithTaskOccurrences;
    isCompleted: boolean;
    progressPercent: number;
    completedTasks: number;
    totalTasks: number;
}) {
    const { i18n, t } = useTranslation();
    const { data: createdByUser } = useQuery(userByIdOptions({ id: list.createdById }));
    const { data: responsibleUser } = useQuery(
        userByIdOptions({ id: list.responsibleUserId ?? null }),
    );

    const percentClass = isCompleted ? "text-chameleon-green" : "text-compendia-foreground";
    const formattedCreatedAt = new Date(list.createdAt).toLocaleDateString(i18n.language, {
        day: "numeric",
        month: "short",
        year: "numeric",
    });

    return (
        <aside className="bg-surface-subtle relative flex flex-col border-t border-compendia-border md:border-t-0 md:border-l md:min-h-0 md:overflow-hidden">
            <button
                type="button"
                onClick={onToggle}
                aria-label={t("common.expand")}
                title={t("common.expand")}
                className={cn(
                    "absolute top-6 right-6 z-10 hidden md:inline-flex size-7 items-center justify-center rounded-full text-compendia-muted hover:text-compendia-foreground hover:bg-compendia-greige-20 cursor-pointer transition-opacity duration-200",
                    open ? "pointer-events-none opacity-0" : "opacity-100",
                )}
            >
                <FontAwesomeIcon icon={faChevronLeft} />
            </button>
            <div
                className={cn(
                    "md:flex-1 md:min-h-0 md:overflow-y-auto p-6 flex flex-col gap-4 w-full md:w-[320px] transition-opacity duration-200",
                    !open && "md:pointer-events-none md:opacity-0",
                )}
            >
                <div className="flex items-center justify-between">
                    <h3 className="text-label font-medium uppercase tracking-wider text-text-secondary">
                        {t("common.details")}
                    </h3>
                    <button
                        type="button"
                        onClick={onToggle}
                        aria-label={t("common.collapse")}
                        title={t("common.collapse")}
                        className="size-7 inline-flex items-center justify-center rounded-full text-compendia-muted hover:text-compendia-foreground hover:bg-compendia-greige-20 cursor-pointer print:hidden"
                    >
                        <FontAwesomeIcon icon={faChevronRight} />
                    </button>
                </div>

                <section className="bg-compendia-card border border-compendia-border rounded-[3px] p-4 flex flex-col gap-2">
                    <div className="flex items-center justify-between">
                        <h3 className="text-label font-medium uppercase tracking-wider text-compendia-foreground">
                            {t("common.progress")}
                        </h3>
                        <span className={`text-sm font-semibold ${percentClass}`}>
                            {progressPercent}%
                        </span>
                    </div>
                    <div className="h-1.5 bg-compendia-greige-60 rounded-full overflow-hidden">
                        <div
                            className={`h-1.5 rounded-full transition-[width] duration-500 ease-out ${
                                isCompleted ? "bg-chameleon-green" : "bg-chameleon-yellow"
                            }`}
                            style={{ width: `${progressPercent}%` }}
                        />
                    </div>
                    <span className="text-xs text-compendia-muted">
                        {completedTasks}/{totalTasks} {t("common.tasks").toLowerCase()}
                    </span>
                </section>

                <section className="bg-compendia-card border border-compendia-border rounded-[3px] p-4 flex flex-col gap-2">
                    <h3 className="text-label font-medium uppercase tracking-wider text-compendia-foreground">
                        {t("common.description")}
                    </h3>
                    <p
                        lang={i18n.language === "nb" ? "nb-NO" : "en"}
                        className="text-sm text-compendia-foreground whitespace-pre-wrap break-words hyphens-auto"
                    >
                        {list.description?.trim()
                            ? list.description
                            : t("common.noDescriptionProvided")}
                    </p>
                </section>

                <section className="bg-compendia-card border border-compendia-border rounded-[3px] p-4 flex flex-col gap-3">
                    <h3 className="text-label font-medium uppercase tracking-wider text-compendia-foreground">
                        {t("common.details")}
                    </h3>
                    <dl className="flex flex-col gap-2 text-sm">
                        <div className="flex justify-between gap-3">
                            <dt className="uppercase tracking-wider text-xs text-compendia-muted">
                                {t("common.created")}
                            </dt>
                            <dd className="text-compendia-foreground text-right">
                                {formattedCreatedAt}
                            </dd>
                        </div>
                        <div className="flex justify-between gap-3">
                            <dt className="uppercase tracking-wider text-xs text-compendia-muted">
                                {t("common.responsible")}
                            </dt>
                            <dd className="text-compendia-foreground text-right">
                                {responsibleUser
                                    ? `${responsibleUser.firstName} ${responsibleUser.lastName}`
                                    : "-"}
                            </dd>
                        </div>
                        <div className="flex justify-between gap-3">
                            <dt className="uppercase tracking-wider text-xs text-compendia-muted">
                                {t("common.createdBy")}
                            </dt>
                            <dd className="text-compendia-foreground text-right">
                                {createdByUser
                                    ? `${createdByUser.firstName} ${createdByUser.lastName}`
                                    : "-"}
                            </dd>
                        </div>
                    </dl>
                </section>
            </div>
        </aside>
    );
}
